import { Alert, Badge, Button, Code, Group, Modal, NumberInput, Paper, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconMathFunction, IconPlus } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";
import { useCan } from "../auth/user";
import { useT } from "../i18n/script";

type IndexDef = components["schemas"]["IndexDef"];

export default function IndicesPage() {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const defs = useQuery({ queryKey: ["index-defs"], queryFn: async () => unwrap(await api.GET("/api/v1/indices/definitions")) });
  const cmaps = useQuery({ queryKey: ["cmaps"], queryFn: async () => unwrap(await api.GET("/api/v1/indices/colormaps")) as string[] });
  const [form, setForm] = useState<IndexDef | null>(null);
  const [check, setCheck] = useState<{ valid: boolean; bands?: string[]; error?: string } | null>(null);

  async function validate(formula: string) {
    const r = unwrap(await api.POST("/api/v1/indices/validate-formula", { params: { query: { formula } } })) as typeof check;
    setCheck(r);
  }
  async function save() {
    try {
      unwrap(await api.POST("/api/v1/indices/definitions", { body: form! }));
      setForm(null); qc.invalidateQueries({ queryKey: ["index-defs"] });
      notifications.show({ color: "green", message: t("Indeks sačuvan") });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={3}>{t("Indeksi i analiza")}</Title>
          <Text size="sm" c="dimmed">{t("Vegetacioni (stablo) i pedološki (tlo) indeksi. Formule su dokumentovane i važe za UAV i Sentinel-2.")}</Text>
        </div>
        {can("indices:manage") && <Button leftSection={<IconPlus size={16} />} onClick={() => {
          setCheck(null);
          setForm({ code: "", name: "", target: "vegetation", formula: "(nir - red) / (nir + red)", value_min: -1, value_max: 1,
            colormap: "rdylgn", required_bands: [], builtin: false });
        }}>{t("Korisnički indeks (Band Math)")}</Button>}
      </Group>
      <Paper withBorder>
        <Table fz="sm" striped verticalSpacing="xs">
          <Table.Thead><Table.Tr><Table.Th>{t("Šifra")}</Table.Th><Table.Th>{t("Naziv")}</Table.Th><Table.Th>{t("Cilj")}</Table.Th>
            <Table.Th>{t("Formula")}</Table.Th><Table.Th>{t("Kanali")}</Table.Th><Table.Th>{t("Opseg")}</Table.Th><Table.Th>{t("Referenca")}</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {(defs.data ?? []).map((d) => (
              <Table.Tr key={d.code}>
                <Table.Td><Group gap={4}><b translate="no">{d.code}</b>{!d.builtin && <Badge size="xs" color="soil">{t("korisnički")}</Badge>}</Group></Table.Td>
                <Table.Td>{d.name}<Text size="xs" c="dimmed">{d.description}</Text></Table.Td>
                <Table.Td><Badge variant="light" color={d.target === "soil" ? "soil" : "agri"}>{d.target === "soil" ? t("tlo") : t("vegetacija")}</Badge></Table.Td>
                <Table.Td><Code>{d.formula}</Code></Table.Td>
                <Table.Td translate="no">{d.required_bands?.join(", ")}</Table.Td>
                <Table.Td>{d.value_min} … {d.value_max}</Table.Td>
                <Table.Td><Text size="xs">{d.reference}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <Modal opened={!!form} onClose={() => setForm(null)} size="lg" title={t("Korisnički indeks")}>
        {form && <Stack>
          <Group grow>
            <TextInput label={t("Šifra")} placeholder="MOJ_INDEKS" translate="no" value={form.code} onChange={(e) => setForm({ ...form, code: e.currentTarget.value.toUpperCase() })} />
            <Select label={t("Cilj")} data={[{ value: "vegetation", label: t("vegetacija") }, { value: "soil", label: t("tlo") }]}
              value={form.target} onChange={(v) => setForm({ ...form, target: (v ?? "vegetation") as IndexDef["target"] })} />
          </Group>
          <TextInput label={t("Naziv")} value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <TextInput label={t("Formula")} description="blue, green, red, rededge, nir, swir1, swir2 · + - * / ** · sqrt abs log exp"
            leftSection={<IconMathFunction size={16} />} value={form.formula}
            onChange={(e) => { setCheck(null); setForm({ ...form, formula: e.currentTarget.value }); }} onBlur={(e) => validate(e.currentTarget.value)} />
          {check && (check.valid ? <Alert color="green" py={6}>{t("Formula je ispravna. Kanali")}: {check.bands?.join(", ")}</Alert>
            : <Alert color="red" py={6}>{check.error}</Alert>)}
          <Group grow>
            <NumberInput label="min" value={form.value_min} onChange={(v) => setForm({ ...form, value_min: Number(v) })} />
            <NumberInput label="max" value={form.value_max} onChange={(v) => setForm({ ...form, value_max: Number(v) })} />
            <Select label={t("Paleta")} searchable data={cmaps.data ?? []} value={form.colormap} onChange={(v) => setForm({ ...form, colormap: v ?? "rdylgn" })} />
          </Group>
          <TextInput label={t("Opis")} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.currentTarget.value })} />
          <Button onClick={save} disabled={!form.code || !form.name}>{t("Sačuvaj")}</Button>
        </Stack>}
      </Modal>
    </Stack>
  );
}
